/**
 * Build a clustered bar chart SVG for ESR section 3.2 (Hours / Cycles by aircraft).
 * Pure string SVG — safe for PDF Handlebars and interactive preview.
 */

function escapeXml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function parseHoursDecimal(row) {
  if (row == null) return 0;
  const direct = Number(row.airborneHoursDecimal);
  if (Number.isFinite(direct)) return Math.max(0, direct);
  const hm = String(row.airborneHours || '').trim();
  const match = hm.match(/^(\d+):(\d{1,2})$/);
  if (match) {
    return Math.max(0, Number(match[1]) + Number(match[2]) / 60);
  }
  const asNum = Number(hm);
  return Number.isFinite(asNum) ? Math.max(0, asNum) : 0;
}

function parseLandings(row) {
  const n = Number(row?.landings);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/**
 * @param {Array<object>} rows - aircraft / airborneHoursDecimal / landings
 * @param {{ title?: string, width?: number, height?: number }} [options]
 */
export function buildEsrHoursLandingsBarChartSvg(rows, options = {}) {
  const data = (Array.isArray(rows) ? rows : [])
    .filter((row) => row && String(row.aircraft || '').toUpperCase() !== 'TOTAL')
    .map((row) => ({
      label: String(row.aircraft || '—'),
      hours: parseHoursDecimal(row),
      landings: parseLandings(row)
    }));

  const width = options.width || 900;
  const height = options.height || 420;
  const margin = { top: 48, right: 24, bottom: 72, left: 56 };
  const plotW = width - margin.left - margin.right;
  const plotH = height - margin.top - margin.bottom;
  const title = options.title || 'HOURS / CYCLES LOGGED AIRCRAFT WISE';

  if (!data.length) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="100%" height="100%" fill="#ffffff"/>
  <text x="${width / 2}" y="${height / 2}" text-anchor="middle" fill="#666" font-family="Arial, sans-serif" font-size="14">No flying data for chart</text>
</svg>`;
  }

  const maxVal = Math.max(
    1,
    ...data.map((d) => Math.max(d.hours, d.landings))
  );
  // Nice ceiling
  const niceMax = Math.ceil(maxVal / 10) * 10 || 10;
  const groupWidth = plotW / data.length;
  const barGap = Math.max(4, groupWidth * 0.08);
  const barWidth = Math.max(6, (groupWidth - barGap * 3) / 2);
  const hoursColor = '#37474f';
  const landingsColor = '#90a4ae';

  const ticks = 6;
  const gridLines = [];
  for (let i = 0; i <= ticks; i += 1) {
    const value = (niceMax / ticks) * i;
    const y = margin.top + plotH - (value / niceMax) * plotH;
    gridLines.push(`<line x1="${margin.left}" y1="${y.toFixed(1)}" x2="${margin.left + plotW}" y2="${y.toFixed(1)}" stroke="#e0e0e0" stroke-width="1"/>`);
    gridLines.push(`<text x="${margin.left - 8}" y="${(y + 4).toFixed(1)}" text-anchor="end" fill="#555" font-family="Arial, sans-serif" font-size="11">${Math.round(value)}</text>`);
  }

  const bars = [];
  const labels = [];
  data.forEach((d, index) => {
    const groupX = margin.left + index * groupWidth + barGap;
    const hoursH = (d.hours / niceMax) * plotH;
    const landingsH = (d.landings / niceMax) * plotH;
    const baseY = margin.top + plotH;
    const hoursY = baseY - hoursH;
    const landingsY = baseY - landingsH;
    const hoursX = groupX;
    const landingsX = groupX + barWidth + barGap;

    bars.push(`<rect x="${hoursX.toFixed(1)}" y="${hoursY.toFixed(1)}" width="${barWidth.toFixed(1)}" height="${Math.max(0, hoursH).toFixed(1)}" fill="${hoursColor}"/>`);
    bars.push(`<rect x="${landingsX.toFixed(1)}" y="${landingsY.toFixed(1)}" width="${barWidth.toFixed(1)}" height="${Math.max(0, landingsH).toFixed(1)}" fill="${landingsColor}"/>`);

    if (d.hours > 0) {
      bars.push(`<text x="${(hoursX + barWidth / 2).toFixed(1)}" y="${(hoursY - 4).toFixed(1)}" text-anchor="middle" fill="#333" font-family="Arial, sans-serif" font-size="9">${d.hours.toFixed(1)}</text>`);
    }
    if (d.landings > 0) {
      bars.push(`<text x="${(landingsX + barWidth / 2).toFixed(1)}" y="${(landingsY - 4).toFixed(1)}" text-anchor="middle" fill="#333" font-family="Arial, sans-serif" font-size="9">${Math.round(d.landings)}</text>`);
    }

    const labelX = groupX + barWidth + barGap / 2;
    labels.push(`<text x="${labelX.toFixed(1)}" y="${(baseY + 18).toFixed(1)}" text-anchor="middle" fill="#333" font-family="Arial, sans-serif" font-size="11">${escapeXml(d.label)}</text>`);
  });

  const legendY = 22;
  const legend = `
  <rect x="${margin.left}" y="${legendY - 10}" width="12" height="12" fill="${hoursColor}"/>
  <text x="${margin.left + 18}" y="${legendY}" fill="#333" font-family="Arial, sans-serif" font-size="12">HOURS</text>
  <rect x="${margin.left + 90}" y="${legendY - 10}" width="12" height="12" fill="${landingsColor}"/>
  <text x="${margin.left + 108}" y="${legendY}" fill="#333" font-family="Arial, sans-serif" font-size="12">CYCLES</text>`;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="100%" height="100%" fill="#ffffff"/>
  <text x="${width / 2}" y="18" text-anchor="middle" fill="#1a1a1a" font-family="Arial, sans-serif" font-size="14" font-weight="700">${escapeXml(title)}</text>
  ${legend}
  ${gridLines.join('\n  ')}
  <line x1="${margin.left}" y1="${margin.top}" x2="${margin.left}" y2="${margin.top + plotH}" stroke="#666" stroke-width="1.5"/>
  <line x1="${margin.left}" y1="${margin.top + plotH}" x2="${margin.left + plotW}" y2="${margin.top + plotH}" stroke="#666" stroke-width="1.5"/>
  <text x="16" y="${margin.top + plotH / 2}" text-anchor="middle" fill="#555" font-family="Arial, sans-serif" font-size="11" transform="rotate(-90 16 ${margin.top + plotH / 2})">HOURS / CYCLES</text>
  <text x="${margin.left + plotW / 2}" y="${height - 12}" text-anchor="middle" fill="#555" font-family="Arial, sans-serif" font-size="11">AIRCRAFT REGISTRATION</text>
  ${bars.join('\n  ')}
  ${labels.join('\n  ')}
</svg>`;
}

export { parseHoursDecimal, parseLandings };
